import {
  Backdrop,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  useTheme,
} from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import React from 'react';
import { useTranslate } from 'react-admin';

import { InventorizationStatusData } from '../services/configApi';
import { getDetailedStatusLabel, getStatusColor } from '../utils/inventorizationHelpers';

const STATUS_BAR_WIDTH = 4;
// Leaves the spinner something to sit on while the first records of a place are on their way.
const MASK_MIN_HEIGHT = 56;

interface InventorizationRecordsListProps {
  records: InventorizationStatusData[];
  selectedId: number | null;
  disabled?: boolean;
  /** Masks the list while it is being fetched, so a stale record is never acted on. */
  isLoading?: boolean;
  /**
   * Show what the scan matched instead of the recorded status. Only initial mode writes the
   * pallet columns, and its status is a fixed value carrying no information.
   */
  showPalletMatch?: boolean;
  /** Omitted in quick mode, where the scanner alone decides which record is worked on. */
  onSelect?: (record: InventorizationStatusData) => void;
  /** Omitted where taking a single pallet back is not offered. */
  onDelete?: (record: InventorizationStatusData) => void;
}

/** Every pallet recorded at the selected place, with the worked-on one highlighted. */
export const InventorizationRecordsList: React.FC<InventorizationRecordsListProps> = ({
  records,
  selectedId,
  disabled,
  isLoading,
  showPalletMatch,
  onSelect,
  onDelete,
}) => {
  const theme = useTheme();
  const translate = useTranslate();

  const mask = (
    <Backdrop
      open={!!isLoading}
      style={{ position: 'absolute', zIndex: 1, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
    >
      <CircularProgress size={28} />
    </Backdrop>
  );

  if (!records.length) {
    return (
      <div style={{ position: 'relative', minHeight: isLoading ? MASK_MIN_HEIGHT : undefined }}>
        {!isLoading && (
          <Typography variant="body2" color="textSecondary">
            {translate('osfc.storage_ui.inventorization.records.empty')}
          </Typography>
        )}
        {mask}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Typography variant="subtitle2" color="textSecondary">
        {translate('osfc.storage_ui.inventorization.records.title', { count: records.length })}
      </Typography>
      <List dense disablePadding>
        {records.map((record) => {
          const isSelected = record.id === selectedId;
          const itemStyle: React.CSSProperties = {
            borderLeft: `${STATUS_BAR_WIDTH}px solid ${getStatusColor(record.status, theme)}`,
            marginTop: '4px',
            backgroundColor: theme.palette.background.paper,
          };
          const isMatchedToPallet = record.pallet_id != null;
          const hasPalletNumber = record.pallet_ext_id != null;
          const itemText = (
            <ListItemText
              primary={
                <span style={{ wordBreak: 'break-all' }}>
                  {record.sscc || translate('osfc.storage_ui.inventorization.records.no_sscc')}
                </span>
              }
              secondary={(
                <>
                  {showPalletMatch ? (
                    <>
                      <div>{translate('osfc.storage_ui.inventorization.records.pallet_number', { value: hasPalletNumber ? record.pallet_ext_id : '-' })}</div>
                      <div>{translate('osfc.storage_ui.inventorization.records.pallet_id', { value: isMatchedToPallet ? record.pallet_id : '-' })}</div>
                      <div>{translate('osfc.storage_ui.inventorization.records.order', { value: record.order_ref ?? record.order_id ?? '-' })}</div>
                      <div>{(isMatchedToPallet ?
                        <span style={{ color: theme.palette.success.main, fontWeight: 'bold'}}>
                          {translate('osfc.storage_ui.inventorization.records.matched')}
                        </span> :
                        <span style={{ color: theme.palette.secondary.main}}>
                          {translate('osfc.storage_ui.inventorization.records.not_matched')}
                        </span>
                      )}</div>
                    </>
                  ) : (
                    <div>{[
                      getDetailedStatusLabel(record.status, record.incorrect_status, translate),
                      record.note,
                    ].filter(Boolean).join(' — ')}</div>
                  )}
                  <div>{translate('osfc.storage_ui.inventorization.records.quantity', { value: record.quantity ?? '-' })}</div>
                </>
              )}
              primaryTypographyProps={{
                style: { fontWeight: isSelected ? 'bold' : 'normal' },
              }}
              // The secondary block is made of divs, which a <p> may not contain
              secondaryTypographyProps={{ component: 'div' }}
            />
          );

          const deleteAction = onDelete && (
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                size="medium"
                disabled={disabled}
                title={translate('osfc.storage_ui.inventorization.records.delete')}
                onClick={() => onDelete(record)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </ListItemSecondaryAction>
          );

          return onSelect ? (
            <ListItem
              key={record.id}
              button
              selected={isSelected}
              style={itemStyle}
              onClick={() => onSelect(record)}
            >
              {itemText}
              {deleteAction}
            </ListItem>
          ) : (
            <ListItem key={record.id} selected={isSelected} style={itemStyle}>
              {itemText}
              {deleteAction}
            </ListItem>
          );
        })}
      </List>
      {mask}
    </div>
  );
};
